import React from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/core/hooks/useModal';
import useIsTouchDevice from '@/core/hooks/useIsTouchDevice';
import Grid from '@/components/molecules/Layout/Grid';
import { ResultH3 } from '@/pages/result/styles';
import CopyTextInside from '@/components/features/CopyTextInside/CopyTextInside';
import ShareButtons from '@/components/features/ShareButtons/ShareButtons';
import A from '@/components/atoms/UI/links/A';
import IconActionButton from '@/components/molecules/IconActionButton/IconActionButton';

interface Props {
    /** Абсолютный канонический URL теста — из API/сервера, НЕ из браузера. */
    shareUrl: string;
    /** Название теста — заголовок для нативного шаринга. */
    title?: string;
}

/**
 * Кнопка «Поделиться тестом» — обычный React-компонент (используется как island
 * на странице теста и напрямую внутри React-страниц).
 *
 * Всплывашка: копирование ссылки на тест + соцсети по локали. Ссылка берётся из
 * пропа (сервер/API), а не из window.location. На тач-устройствах с Web Share
 * API — нативный системный шит (progressive enhancement).
 */
const TestShareButton: React.FC<Props> = ({ shareUrl, title }) => {
    const { t } = useTranslation();
    const { show, RenderModal } = useModal();
    const isTouch = useIsTouchDevice();

    const text_share = t('testItem.share');
    const text_copy_link = t('testItem.share_copy_link');

    const handleClick = () => {
        const canNativeShare = isTouch
            && typeof navigator !== 'undefined'
            && typeof navigator.share === 'function';

        if (canNativeShare) {
            // Отмена пользователем отклоняет промис — это не ошибка, глушим.
            navigator.share({ title, url: shareUrl }).catch(() => { /* cancelled */ });
            return;
        }

        show();
    };

    return (
        <>
            <RenderModal>
                <Grid col="1" gap="4">
                    <Grid col="1" gap="2">
                        <ResultH3>{text_copy_link}:</ResultH3>
                        <CopyTextInside><A href={shareUrl}>{shareUrl}</A></CopyTextInside>
                    </Grid>

                    <Grid col="1" gap="2">
                        <ResultH3>{text_share}:</ResultH3>
                        <ShareButtons url={shareUrl} />
                    </Grid>
                </Grid>
            </RenderModal>

            <IconActionButton iconName="Share" label={text_share} onClick={handleClick} />
        </>
    );
};

export default TestShareButton;
